var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });

// src/types/workflow/transition-schema.types.ts
import { z } from "zod";
var FieldType = {
  TEXTAREA: "textarea",
  FILE_UPLOAD: "file_upload",
  SELECT: "select",
  CONCLUSION_SELECT: "conclusion_select",
  COMMENT: "comment"
};
var ButtonType = {
  SUBMIT: "submit",
  CANCEL: "cancel"
};
var TriggerType = {
  BUTTON: "button"
};
var StyleVariant = {
  PRIMARY: "primary",
  SECONDARY: "secondary",
  GHOST: "ghost"
};
var TransitionActionType = {
  DIALOG: "dialog"
};
var zBaseField = z.object({
  name: z.string(),
  label_key: z.string(),
  placeholder_key: z.string().optional(),
  description_key: z.string().optional(),
  required: z.boolean(),
  default_value: z.unknown().optional(),
  disabled: z.boolean().optional()
});
var zTextAreaValidation = z.object({
  min_length: z.number().positive().optional(),
  max_length: z.number().positive().optional(),
  pattern: z.string().optional()
});
var zTextAreaField = zBaseField.extend({
  type: z.literal(FieldType.TEXTAREA),
  validation: zTextAreaValidation.optional()
});
var zFileUploadValidation = z.object({
  accepted_formats: z.array(z.string()).min(1),
  max_size_mb: z.number().positive()
});
var zFileUploadField = zBaseField.extend({
  type: z.literal(FieldType.FILE_UPLOAD),
  validation: zFileUploadValidation
});
var zSelectOption = z.object({
  value: z.string(),
  label_key: z.string(),
  description_key: z.string().optional(),
  disabled: z.boolean().optional().default(false)
});
var zSelectField = zBaseField.extend({
  type: z.literal(FieldType.SELECT),
  options: z.array(zSelectOption).min(1),
  multiple: z.boolean().optional().default(false)
});
var zConclusionSelectField = zBaseField.extend({
  type: z.literal(FieldType.CONCLUSION_SELECT),
  options: z.array(z.any()).optional(),
  multiple: z.boolean().optional().default(false)
});
var zCommentValidation = z.object({
  content: zTextAreaValidation,
  attachment: zFileUploadValidation.extend({
    required: z.boolean()
  })
});
var zCommentField = zBaseField.extend({
  type: z.literal(FieldType.COMMENT),
  validation: zCommentValidation.optional()
});
var zField = z.discriminatedUnion("type", [
  zTextAreaField,
  zFileUploadField,
  zSelectField,
  zConclusionSelectField,
  zCommentField
]);
var zDialogButton = z.object({
  type: z.enum([
    ButtonType.SUBMIT,
    ButtonType.CANCEL
  ]),
  label_key: z.string().optional(),
  action: z.string(),
  variant: z.enum([
    StyleVariant.PRIMARY,
    StyleVariant.SECONDARY,
    StyleVariant.GHOST
  ]).optional()
});
var zTransitionAction = z.object({
  type: z.literal(TransitionActionType.DIALOG),
  title_key: z.string(),
  description_key: z.string().optional(),
  fields: z.array(zField),
  buttons: z.array(zDialogButton)
});
var zTransitionTrigger = z.object({
  type: z.literal(TriggerType.BUTTON),
  icon: z.string().optional(),
  label_key: z.string(),
  variant: z.enum([
    StyleVariant.PRIMARY,
    StyleVariant.SECONDARY,
    StyleVariant.GHOST
  ]).optional()
});
var zTransitionOptions = z.object({
  autoAssign: z.object({
    owner: z.boolean().optional().default(false)
  }).optional()
});
var zTransition = z.object({
  id: z.string(),
  trigger: zTransitionTrigger,
  action: z.union([
    zTransitionAction,
    z.object({}).strict()
  ]),
  options: zTransitionOptions.optional().default({})
});

// src/types/workflow/step-schema.types.ts
var WorkflowStepVariant = {
  STRIPED_GRAY: "striped-gray",
  GRAY: "gray",
  BLUE: "blue",
  YELLOW: "yellow"
};

// src/types/rich-text.ts
import z2 from "zod";
var zMentionAttrs = z2.object({
  id: z2.string(),
  label: z2.string(),
  mentionSuggestionChar: z2.string().optional()
});
var zRichTextMark = z2.object({
  type: z2.literal([
    "bold",
    "italic",
    "underline",
    "link",
    "textStyle",
    "mention"
  ]),
  attrs: z2.record(z2.string(), z2.unknown()).optional()
});
var RICH_TEXT_NODE_TYPES = [
  "doc",
  "paragraph",
  "text",
  "mention",
  "heading",
  "bulletList",
  "listItem",
  "hardBreak"
];
var zRichTextNode = z2.lazy(() => z2.object({
  type: z2.union(RICH_TEXT_NODE_TYPES.map((i) => z2.literal(i))),
  marks: z2.array(zRichTextMark).optional(),
  attrs: zMentionAttrs.optional(),
  text: z2.string().optional(),
  content: z2.array(zRichTextNode).optional()
}));
var zRichText = z2.object({
  type: z2.literal("doc"),
  content: z2.array(zRichTextNode)
});

// src/types/opaque.ts
function UserId(id) {
  return id;
}
__name(UserId, "UserId");
function UserAuthIdentityId(id) {
  return id;
}
__name(UserAuthIdentityId, "UserAuthIdentityId");
function OrgId(id) {
  return id;
}
__name(OrgId, "OrgId");
function OrgAuthIdentityId(id) {
  return id;
}
__name(OrgAuthIdentityId, "OrgAuthIdentityId");

// src/utils/rich-text.utils.ts
import { validate as uuidValidate, version as uuidVersion } from "uuid";
var RichTextUtils = class {
  static {
    __name(this, "RichTextUtils");
  }
  static extractMentions(content) {
    if (!content.content?.length) {
      return [];
    }
    const mentions = /* @__PURE__ */ new Set();
    const traverseNodes = /* @__PURE__ */ __name((nodes) => {
      for (const node of nodes) {
        if (node.type === "mention" && node.attrs) {
          if (node.attrs.id && isValidUUID(node.attrs.id)) {
            mentions.add(UserId(node.attrs.id));
          }
        }
        if (Array.isArray(node.content)) {
          traverseNodes(node.content);
        }
      }
    }, "traverseNodes");
    traverseNodes(content.content);
    return Array.from(mentions);
  }
  static getTextContent(content) {
    if (!content?.content?.length) {
      return "";
    }
    let rawText = "";
    const extractText = /* @__PURE__ */ __name((nodes) => {
      for (const node of nodes) {
        if (node.type === "mention" && node.attrs && node.attrs.label) {
          rawText += `${node.attrs.mentionSuggestionChar ?? ""}${node.attrs.label} `;
        } else if ("text" in node && typeof node.text === "string") {
          rawText += `${node.text} `;
        }
        if (Array.isArray(node.content)) {
          extractText(node.content);
        }
      }
    }, "extractText");
    extractText(content.content);
    return rawText.trim();
  }
};
function isValidUUID(uuid) {
  return uuidValidate(uuid) && [
    4,
    7
  ].includes(uuidVersion(uuid));
}
__name(isValidUUID, "isValidUUID");

// src/utils/short-id.ts
function shortId(longId) {
  return longId.slice(-8);
}
__name(shortId, "shortId");

// src/enums/user-sort-field.ts
var UserSortField = /* @__PURE__ */ (function(UserSortField2) {
  UserSortField2["FIRST_NAME"] = "firstName";
  UserSortField2["LAST_NAME"] = "lastName";
  UserSortField2["EMAIL"] = "email";
  UserSortField2["CREATED_AT"] = "createdAt";
  UserSortField2["LAST_ACTIVITY"] = "lastActivity";
  return UserSortField2;
})({});

// src/enums/operation.ts
var OperatorEnum = /* @__PURE__ */ (function(OperatorEnum2) {
  OperatorEnum2["EQ"] = "eq";
  OperatorEnum2["CONTAINS_SUBSTR"] = "contains_substr";
  OperatorEnum2["GT"] = "gt";
  OperatorEnum2["LT"] = "lt";
  OperatorEnum2["GTE"] = "gte";
  OperatorEnum2["LTE"] = "lte";
  OperatorEnum2["BETWEEN"] = "between";
  OperatorEnum2["IN"] = "in";
  return OperatorEnum2;
})({});

// src/enums/permissions.ts
var GlobalPermission = /* @__PURE__ */ (function(GlobalPermission2) {
  GlobalPermission2["OrganizationManage"] = "spz:admin:organization:manage";
  GlobalPermission2["CompaniesManage"] = "spz:admin:companies:manage";
  GlobalPermission2["AccessControlsManage"] = "spz:admin:access_controls:manage";
  GlobalPermission2["UsersManage"] = "spz:admin:users:manage";
  GlobalPermission2["RolesManage"] = "spz:admin:roles:manage";
  GlobalPermission2["UserGroupsManage"] = "spz:admin:user_groups:manage";
  GlobalPermission2["CompanyGroupsManage"] = "spz:admin:company_groups:manage";
  GlobalPermission2["PermissionsManage"] = "spz:admin:permissions:manage";
  return GlobalPermission2;
})({});
var CompanyPermission = /* @__PURE__ */ (function(CompanyPermission2) {
  CompanyPermission2["ControlView"] = "spz:control:view";
  CompanyPermission2["ControlResultSkip"] = "spz:control-result:skip";
  CompanyPermission2["ControlResultStartInvestigation"] = "spz:control-result:start-investigation";
  CompanyPermission2["ControlResultCompleteInvestigation"] = "spz:control-result:complete-investigation";
  CompanyPermission2["ControlResultManageOwnership"] = "spz:control-result:manage-ownership";
  CompanyPermission2["AnalysisReportExport"] = "spz:analysis-report:export";
  CompanyPermission2["ControlResultChangePriority"] = "spz:control-result:change-priority";
  return CompanyPermission2;
})({});

// src/enums/sort-direction.ts
var SortDirection = /* @__PURE__ */ (function(SortDirection2) {
  SortDirection2["ASC"] = "ASC";
  SortDirection2["DESC"] = "DESC";
  return SortDirection2;
})({});

// src/enums/user-account-type.ts
var UserAccountType = /* @__PURE__ */ (function(UserAccountType2) {
  UserAccountType2["ADMIN"] = "admin";
  UserAccountType2["BASIC_USER"] = "basic_user";
  return UserAccountType2;
})({});

// src/builders/rich-text.builder.ts
var RichTextBuilder = class _RichTextBuilder {
  static {
    __name(this, "RichTextBuilder");
  }
  content = [];
  static new() {
    return new _RichTextBuilder();
  }
  addText(text) {
    this.content.push({
      type: "text",
      text
    });
    return this;
  }
  addMention(uuid, label) {
    this.content.push({
      type: "mention",
      attrs: {
        id: uuid,
        label,
        mentionSuggestionChar: "@"
      }
    });
    return this;
  }
  addParagraph(content) {
    this.content.push({
      type: "paragraph",
      content
    });
    return this;
  }
  addCustomType(type, attrs) {
    this.content.push({
      type,
      attrs
    });
    return this;
  }
  build() {
    return {
      type: "doc",
      content: this.content
    };
  }
  nodes() {
    return this.content;
  }
};
export {
  ButtonType,
  CompanyPermission,
  FieldType,
  GlobalPermission,
  OperatorEnum,
  OrgAuthIdentityId,
  OrgId,
  RichTextBuilder,
  RichTextUtils,
  SortDirection,
  StyleVariant,
  TransitionActionType,
  TriggerType,
  UserAccountType,
  UserAuthIdentityId,
  UserId,
  UserSortField,
  WorkflowStepVariant,
  shortId,
  zRichText,
  zTransition
};
//# sourceMappingURL=index.js.map